import Spinner from "../../../../components/Spinner.jsx";
import {useTranslation} from "react-i18next";
import {useContext, useEffect, useState} from "react";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import Button from "../../../../components/Button.jsx";
import Select from "react-select";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import googleLogo from "../../../../images/login/g.svg";
import {IntegrationActions, IntegrationConnectButton, IntegrationSection, OAuthConnectionStatus} from "../IntegrationDetailUi.jsx";

export default function GoogleIntegrationForm({project, projectIntegration, refreshOAuthResources}) {

    let {t} = useTranslation();
    let customerClientDefaultValues = [];
    let customerClientOptions = [];
    let unavailableCustomerClients = [];

    for (let customerClient of projectIntegration?.googleIntegrationCustomerClients ?? []) {
        const option = {
            customerId: customerClient.customerId,
            customerClientId: customerClient.customerClientId,
            value: customerClient.customerId + customerClient.customerClientId,
            label: '[' + customerClient.customerClientId + '] ' + customerClient.name
        };

        if (customerClient.availabilityReason) {
            unavailableCustomerClients.push({...option, availabilityReason: customerClient.availabilityReason});
            continue;
        }

        customerClientOptions.push(option);

        if (customerClient.enabled) {
            customerClientDefaultValues.push(option);
        }
    }

    let [selectedCustomerClients, setSelectedCustomerClients] = useState(customerClientDefaultValues);
    useEffect(() => {
        setSelectedCustomerClients(customerClientDefaultValues);
    }, [projectIntegration?.googleIntegrationCustomerClients]);

    const {context, getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);

    let redirectToLogin = () => {
        let oAuthUrl = new URL(import.meta.env.VITE_AUTHORITY_URL);
        oAuthUrl.pathname = '/api/v1/project/integrations/google';

        let query = new URLSearchParams({
            authorization: context.currentIdentity?.token,
            projectId: project.id,
        });

        if (projectIntegration?.id) {
            query.set('integrationId', projectIntegration.id);
        }

        oAuthUrl.search = query.toString();

        window.location.href = oAuthUrl.toString();
    };

    useEffect(() => {
        if (!projectIntegration) {
            setTimeout(
                () => redirectToLogin(),
                600, // so it looks we actually do something too :D
            )
        }
    }, []);

    let [state, setDownloading] = useDownloadState();

    let saveCustomerClients = () => {

        let customerClientIds = [];

        for (let customerClientOption of selectedCustomerClients) {
            customerClientIds.push(customerClientOption.customerClientId);
        }


        setDownloading(true);
        new ProjectIntegrationsApi(getAuthorityConfiguration())
                .chooseGoogleCustomerClients({
                projectId: project.id,
                integrationId: projectIntegration.id,
                requestBody: customerClientIds
            })
            .then(() => {
                resetCurrentUserProjects();
            })
            .finally(() => setDownloading(false))
    };

    const refreshCustomerClients = () => {
        setDownloading(true);
        Promise.resolve(refreshOAuthResources())
            .finally(() => setDownloading(false));
    };

    if (!projectIntegration) {
        return (
            <div>
                <Spinner size={'spinner-large'} className={'mx-auto'}/>
                <p className={'text-center mt-4'}>{t('redirecting')}...</p>
            </div>
        );
    }

    const reconnectRequired = !projectIntegration.authorizationSuccessful;
    const hasNoDiscoveredAccounts = !reconnectRequired && customerClientOptions.length === 0;

    // noinspection JSValidateTypes
    return (
        <LoadingComponent loading={state.downloading}>
            <div className={'flex flex-col gap-5'}>
                <IntegrationSection
                    title={'Připojení ke Google Ads'}
                    description={t('project.google.oauth-help')}
                >
                    <OAuthConnectionStatus
                        connected={!reconnectRequired}
                        connectedDescription={t('project.google.oauth-connected-help')}
                        action={<IntegrationConnectButton logo={googleLogo} logoAlt={'Google'} label={reconnectRequired ? 'Připojit' : 'Obnovit připojení'} onClick={redirectToLogin}/>}
                    />
                </IntegrationSection>

                <IntegrationSection
                    title={t('project.google.enabled-client-customers')}
                    description={t('project.google.enabled-client-customers-help')}
                >
                    <Select
                        value={selectedCustomerClients}
                        isMulti
                        name="colors"
                        options={customerClientOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder={null}
                        noOptionsMessage={() => t('project.integration-no-options')}
                        onChange={setSelectedCustomerClients}
                    />
                    {unavailableCustomerClients.length > 0 && (
                        <div className={'mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-950 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100'}>
                            <div className={'text-sm font-semibold'}>{t('project.google.unavailable-accounts-title')}</div>
                            <p className={'mt-1 text-xs leading-5 text-red-800 dark:text-red-200'}>{t('project.google.unavailable-accounts-help')}</p>
                            <div className={'mt-3 flex flex-wrap gap-2'}>
                                {unavailableCustomerClients.map((customerClient) => (
                                    <div key={customerClient.value} className={'inline-flex max-w-full items-center rounded-md border border-red-300 bg-white px-2 py-1 text-sm text-red-900 dark:border-red-700 dark:bg-red-950/50 dark:text-red-100'}>
                                        <span className={'min-w-0 truncate'}>{customerClient.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {hasNoDiscoveredAccounts && (
                        <div className={'mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'}>
                            Google nevrátil žádný dostupný aktivní klientský účet. Zkontrolujte přihlášený Google účet nebo aktualizujte seznam.
                        </div>
                    )}
                    <div className={'mt-5'}>
                        <IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button'} onClick={refreshCustomerClients}>Aktualizovat seznam</Button>}>
                            <Button className={'button-primary'} onClick={() => saveCustomerClients()}>
                                Uložit výběr
                            </Button>
                        </IntegrationActions>
                    </div>
                </IntegrationSection>

            </div>
        </LoadingComponent>
    );
}
